declare let XRegExp: any;

import Class from '@/Class';
import ComponentManager from '@/Management/ComponentManager';
import { FactoryParameter, FactoryParameterParameter, IFactoryConstructorParameter } from '#/CoreFrameInterfaces';
import { Dictionary } from '#/DefaultViewExtensionInterfaces';
import * as interfaces from '#/CoreFrameInterfaces';

/**
* Repräsentiert die Oberklasse aller Factories und bietet statische Methoden zur Erzeugung von Factory-Instanzen
*/
export default class Factory extends Class {

    public isControlFactory: boolean;

    public isLayoutFactory: boolean;

    public isGroupFactory: boolean;

    /** 
    * Ein eindeutiger Name zur Identifikation im DOM
    */
    public locator: KnockoutObservable<string>;
    constructor(params: IFactoryConstructorParameter) {
        super();
        this.locator = this.observableOrDefault(params.locator, '');

        // TODO: Diese Properties sind auch zu entfernen, wenn alle Unterklasse auf TS protiert sind
        // instanceof GroupFactory funktioniert beispielsweise mit einer JS-Implementierung nicht
        this.isControlFactory = false;
        this.isLayoutFactory = false;
        this.isGroupFactory = false;
    }

    private static uncap(str: string): string {
        return str.substr(0, 1).toLowerCase() + str.substr(1);
    }

    private static parseQueryString(query): Dictionary<FactoryParameterParameter> {
        let result: Dictionary<FactoryParameterParameter> = {};

        /**
         * [0] IsCollapsed=false&
         * [1] Layout=FlowLayout(?Mode=Default&Orientation=Horizontal)
         */
        let matches = XRegExp.match(query, /([\w\d]+=[\w\d\,.\-\*\%',\s=]+(?:\(\?[\w\d\,.'&=]+\))?)(?:[&])?/g);

        for (const match of matches) {
            /**
             * ==> Layout=FlowLayout(?Mode=Default&Orientation=Horizontal)
             */
            let thisParamQuery = match.replace(/&$/, '');

            let paramName = thisParamQuery;
            let paramValue = null;
            let paramParams = null;
            let valueIndex = thisParamQuery.indexOf('=');

            if (valueIndex > -1) {
                /**
                 * ==> Layout
                 */
                paramName = thisParamQuery.substring(0, valueIndex);

                /**
                 * ==> FlowLayout(?Mode=Default&Orientation=Horizontal)
                 */
                paramValue = thisParamQuery.substring(valueIndex + 1, thisParamQuery.length);

                /**
                 * [0] FlowLayout
                 * [1] Mode=Default&Orientation=Horizontal
                 */
                let subParam = XRegExp.match(paramValue, /([\w\d\,.\-',\s]+)\(\?([\w\d\,.'&=]+)?\)/);

                if (subParam != null && subParam.length === 3) {
                    paramValue = subParam[1];
                    paramParams = Factory.parseQueryString(subParam[2]);
                }

                /**
                 * If the value is boolean, it must be parsed
                 */
                if (['true', 'false'].indexOf(paramValue.toLowerCase()) > -1) {
                    paramValue = JSON.parse(paramValue.toLowerCase());
                }
            }

            if (paramParams != null) {
                result[Factory.uncap(paramName)] = {
                    value: paramValue,
                    parameters: paramParams
                };
            } else {
                result[Factory.uncap(paramName)] = paramValue;
            }
        }

        return result;
    }

    /**
     * Parsed einen String im angegebenen Format rekursiv und wandelt ihn in ein Objekt um
     * Format: ExpanderGroup?IsCollapsed=false&Layout=FlowLayout(?Mode=Default&Orientation=Horizontal)
     */
    public static parseUIGeneratorQuery(query: string): FactoryParameter {
        let uiGenerator = query ?? '';
        let parameters = null;
        const paramIndex = uiGenerator.indexOf('?');

        if (paramIndex > -1) {
            uiGenerator = query.substring(0, paramIndex);

            query = query.substring(paramIndex + 1, query.length);
            parameters = Factory.parseQueryString(query);
        }

        uiGenerator = uiGenerator.replace(/Factory$/, '');
        uiGenerator = uiGenerator.replace(/\($/, '');

        return {
            uiGenerator: uiGenerator,
            parameters: parameters
        };
    }

    public static toUIGeneratorQuery(parameter: FactoryParameter): string {
        let query = parameter.uiGenerator;

        const parameterKeys = _.keys(parameter.parameters);
        if (parameterKeys.length > 0) {
            query += '?' + parameterKeys
                .filter(key => parameter.parameters[key] != null)
                .map(key => key + '=' + this.toUIGeneratorQueryParameter(parameter.parameters[key])).join('&');
        }

        return query;
    }
    
    private static toUIGeneratorQueryParameter(parameter: FactoryParameterParameter | any): string {
        if (typeof parameter == 'string')
            return parameter;
        
        if (typeof parameter == 'object' && 'value' in parameter) {
            const parameterParameter = parameter as FactoryParameterParameter;
            let result = parameterParameter.value;
            if (parameterParameter.parameters && parameterParameter.parameters.length) {
                result += `(${parameterParameter.parameters
                    .filter(p => p != null)
                    .map(p => this.toUIGeneratorQueryParameter(p)).join('&')})`
            }
            return result;
        }
        
        return parameter.toString();
    }

    /**
     * Erzeugt eine neue Instanz einer Factory anhand eines QueryStrings
     */
    public static createInstance<T = Factory>(queryString: string, params: interfaces.IControlFactoryConstructorParameter |
        interfaces.IGroupFactoryConstructorParameter |
        interfaces.ILayoutFactoryConstructorParameter |
        interfaces.IViewFactoryConstructorParameter |
        interfaces.ISummaryViewFactoryConstructorParameter, fallback?: string): T {
        let parser = Factory.parseUIGeneratorQuery(queryString);
        let options = _.extend(params || {}, parser.parameters);

        let Component = ComponentManager.getFactory(parser.uiGenerator) as any;

        if (Component == null && fallback != null) {
            Component = ComponentManager.getFactory(fallback);
            console.log('Factory "' + queryString + '" wurde nicht gefunden. Fallback "' + fallback + ' wird verwendet."')
        }

        if (Component == null) {
            throw 'Factory-Komponente "' + queryString + '" wurde nicht gefunden und es wurde kein Fallback definiert.';
        }

        if (options.detailViewItem == null) {
            options.detailViewItem = {
                uiGenerator: queryString
            };
        }

        return new Component(options);
    }
}